import React, { useState, useEffect, useContext, useCallback } from "react";
import { Card, TextField, FormLayout, Stack, Button, Heading, Link, Checkbox, Banner } from "@shopify/polaris";
import { useMutation } from "@apollo/react-hooks";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context/privateContext";

// import Constant
import constant from "lib/constant/constant";

import { errorHelper } from "lib/helpers";

// import helpers
import baseHelper from "lib/helpers/base";
import config from "configuration";

import { PaymentContext } from "app/setup/modules/operator/features/paymentService/context";

// import validate
import validate from "app/setup/modules/operator/features/paymentService/yup";

// import Gql
import { CONNECT_PAYPAL_SELLER, DISCONNECT_PAYPAL } from "app/setup/apollo/mutations";

import { paypalTextFields } from "./fieldConfig";

const PaypalSection = () => {
  const { gql, PAYPAL } = constant;
  const { setBanner } = useContext(PaymentContext);
  const { currentUser = {}, cms = {} } = useContext(PrivateContext);
  const { clientId = "", secret = "" } = (currentUser && currentUser.paypalKey) || {};
  const [connectToPaypal, { loading: paypalLoading }] = useMutation(CONNECT_PAYPAL_SELLER);
  const [disconnectFromPaypal, { loading: disconnectPaypalLoading }] = useMutation(DISCONNECT_PAYPAL);
  const [isPaypalConnected, setIsPaypalConnected] = useState(!!(clientId && secret));
  const [values, setValues] = useState({
    paypalClientId: isPaypalConnected ? clientId : "",
    paypalSecret: isPaypalConnected ? secret : "",
  });
  const [uri, setUri] = useState({
    paypalUri: false,
    customPaypalUri: false,
  });
  const [errorMessage, setErrorMessage] = useState({});
  const [button, setButton] = useState(true);

  const isPaypalError = useCallback(() => Object.values(errorMessage).some((error) => error), [errorMessage]);
  const isPaypalValuesFilled = useCallback(() => !!(values.paypalClientId && values.paypalSecret), [
    values.paypalClientId,
    values.paypalSecret,
  ]);

  useEffect(() => {
    setButton(isPaypalError() || !isPaypalValuesFilled());
  }, [errorMessage, isPaypalError, isPaypalValuesFilled]);

  const handleValueChange = (field, value) => {
    setValues((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleOnBlurChange = async (field, value) => {
    const error = await validate(field, value && value.trim(), cms);
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: error,
    }));
  };
  const paypalFields = paypalTextFields(cms) || [];

  const handlePaypalConnect = () => {
    connectToPaypal({ variables: { input: { clientId: values.paypalClientId, secret: values.paypalSecret } } })
      .then((res) => {
        const resData = baseHelper.getResponseData(res.data, gql.CONNECT_PAYPAL_SELLER);
        let banner = { isOpen: true, title: cms("message.success.connectedPaypal"), status: constant.SUCCESS };
        setIsPaypalConnected(resData);
        if (!resData) {
          const error = baseHelper.getResponseError(res.data, gql.CONNECT_PAYPAL_SELLER);
          banner = { isOpen: true, title: error, status: constant.CRITICAL };
        }
        setBanner({ isOpen: banner.isOpen, title: banner.title, status: banner.status });
      })
      .catch((exception) => {
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };
  const handlePaypalDisconnect = () => {
    disconnectFromPaypal({ variables: { input: { paymentMethod: PAYPAL } } })
      .then((res) => {
        const resData = baseHelper.getResponseData(res.data, gql.DISCONNECT_PAYPAL);
        let banner = { isOpen: true, title: cms("message.success.disconnectPaypal"), status: constant.SUCCESS };
        setIsPaypalConnected(!resData);
        if (!resData) {
          const error = baseHelper.getResponseError(res.data, gql.DISCONNECT_PAYPAL);
          banner = { isOpen: true, title: error, status: constant.CRITICAL };
        }
        setBanner({ isOpen: banner.isOpen, title: banner.title, status: banner.status });
      })
      .catch((exception) => {
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };
  return (
    <>
      <Card
        sectioned
        title={
          (!isPaypalConnected && cms("section.label.connectPaypal")) ||
          (isPaypalConnected && cms("section.label.connectedPaypal"))
        }
      >
        {!isPaypalConnected && (
          <FormLayout>
            <Banner
              id="paypalBanner"
              title={cms("section.label.paypalDoc")}
              action={{
                content: cms("section.label.readDocumentation"),
                url: constant.PAYPAL_BANNER_URL,
              }}
              status={constant.CRITICAL}
            >
              <p id="paypalBannerDescription">{cms("section.content.paypalBanner")}</p>
            </Banner>
            <Stack.Item>
              <Heading>
                <span id="paypalStep1">{cms("section.label.step1")}</span>
              </Heading>
              <p id="paypalDeveloperAccountLink">
                {cms("section.label.clientIdAndSecretPaypal")}
                <Link id="paypalUrl" external url={constant.PAYPAL_DEVELOPER_URL}>
                  {` ${cms("message.success.paypalDeveloper")}`}
                </Link>
              </p>
            </Stack.Item>
            {paypalFields.map((textField) => (
              <TextField
                id={textField.key}
                key={textField.key}
                label={textField.label}
                value={values[textField.key]}
                placeholder={textField.placeholder}
                onChange={(value) => handleValueChange(textField.key, value)}
                onBlur={() => handleOnBlurChange(textField.key, values[textField.key])}
                error={errorMessage && errorMessage[textField.key]}
              />
            ))}
            <Stack.Item>
              <Heading>
                <span id="paypalStep2">{cms("section.label.step2")}</span>
              </Heading>
              <p id="paypalHeading">{cms("section.label.addRedirectPaypal")}</p>
            </Stack.Item>
            <Checkbox
              id="customPaypalUri"
              label={`${config.rootURL}/paypal/callback`}
              checked={uri.customPaypalUri}
              onChange={() => setUri((prevState) => ({ ...prevState, customPaypalUri: !uri.customPaypalUri }))}
            />
            <Stack.Item>
              <Heading>
                <span id="paypalStep3">{cms("section.label.step3")}</span>
              </Heading>
              <p id="paypalLogInfo">{cms("section.content.paypalLogInInformation")}</p>
            </Stack.Item>
            <Checkbox
              id="paypalEmailUri"
              label={cms("section.content.paypalLogInInformation")}
              checked={uri.paypalUri}
              onChange={() => setUri((prevState) => ({ ...prevState, paypalUri: !uri.paypalUri }))}
            />
            <Stack.Item>
              <b>{cms("section.content.forMoreSafer")}</b>
            </Stack.Item>
            <Stack>
              <Stack.Item fill />
              <Stack.Item>
                <Button
                  id="paypalConnectButton"
                  primary
                  loading={paypalLoading}
                  disabled={button || !uri.paypalUri || !uri.customPaypalUri}
                  onClick={() => handlePaypalConnect()}
                >
                  {cms("section.button.connect")}
                </Button>
              </Stack.Item>
            </Stack>
          </FormLayout>
        )}
        {isPaypalConnected && (
          <FormLayout>
            <Stack.Item>
              {paypalFields.map((textField) => (
                <TextField
                  id={textField.key}
                  key={textField.key}
                  label={textField.label}
                  value={values[textField.key]}
                  disabled={isPaypalConnected}
                />
              ))}
            </Stack.Item>
            <Stack>
              <Stack.Item fill />
              <Stack.Item>
                <Button
                  id="paypalDisconnectButton"
                  destructive
                  loading={disconnectPaypalLoading}
                  onClick={() => handlePaypalDisconnect()}
                >
                  {cms("section.button.disconnect")}
                </Button>
              </Stack.Item>
            </Stack>
          </FormLayout>
        )}
      </Card>
    </>
  );
};

export default withFeature(withErrorBoundary(PaypalSection), { feature: constant.PAYPAL });
